import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Stack,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useAddEpisodeToProject, useUploadAudioFile } from '@/hooks/usePodcasts'

const MAX_UPLOAD_BYTES = 50 * 1024 * 1024

interface ImportAudioDialogProps {
  open: boolean
  onClose: () => void
  projectId: number
  projectLanguage: string
}

function isValidHttpUrl(value: string): boolean {
  return /^https?:\/\/.+/i.test(value.trim())
}

/**
 * ImportAudioDialog lets a user link an external audio URL into a project as a
 * podcast episode, without going through the RSS-search flow. It saves via the
 * same useAddEpisodeToProject() mutation PodcastSearch.tsx uses, constructing a
 * minimal SavePodcastPayload from the two user-entered fields (Title + URL).
 */
export const ImportAudioDialog = ({ open, onClose, projectId, projectLanguage }: ImportAudioDialogProps) => {
  const { t } = useTranslation('projects')
  const [title, setTitle] = useState('')
  const [url, setUrl] = useState('')
  const [mode, setMode] = useState<'url' | 'upload'>('url')
  const [file, setFile] = useState<File | null>(null)
  const { mutate: addEpisode, isPending } = useAddEpisodeToProject()
  const { mutate: uploadAudioFile, isPending: isUploadPending } = useUploadAudioFile()

  const canSubmit = title.trim().length > 0 && isValidHttpUrl(url) && !isPending
  const canSubmitUpload =
    title.trim().length > 0 && !!file && file.size <= MAX_UPLOAD_BYTES && !isUploadPending

  const handleClose = () => {
    if (isPending || isUploadPending) return
    setTitle('')
    setUrl('')
    setFile(null)
    setMode('url')
    onClose()
  }

  const handleSubmit = () => {
    if (!canSubmit) return
    const nowSeconds = Math.floor(Date.now() / 1000)
    addEpisode(
      {
        projectId,
        payload: {
          podcast: {
            uuid: crypto.randomUUID(),
            name: title.trim(),
            language: projectLanguage,
            rssUrl: '',
            imageUrl: '',
            websiteUrl: '',
            description: '',
          },
          episode: {
            uuid: crypto.randomUUID(),
            datePublished: nowSeconds,
            name: title.trim(),
            description: '',
            imageUrl: '',
            subTitle: '',
            audioUrl: url.trim(),
            videoUrl: '',
            fileLength: 0,
            fileType: '',
            duration: 0,
            isExplicit: false,
          },
        },
      },
      {
        onSuccess: () => {
          setTitle('')
          setUrl('')
          onClose()
        },
      },
    )
  }

  const handleUploadSubmit = () => {
    if (!canSubmitUpload || !file) return
    const formData = new FormData()
    formData.append('title', title.trim())
    formData.append('file', file)
    uploadAudioFile(
      { projectId, formData },
      {
        onSuccess: () => {
          setTitle('')
          setFile(null)
          onClose()
        },
      },
    )
  }

  const pending = mode === 'url' ? isPending : isUploadPending

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('importAudioTitle', 'Import Audio')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/*
            The Upload File mode/toggle is temporarily disabled: Fly.io's edge
            proxy enforces an undocumented ~1MB request body cap, which broke
            uploads for any real-world audio file before this app's own 50MB
            check ever ran. Fixing it properly requires a direct-to-S3
            presigned-upload flow, judged not worth the effort for now.
            All upload code (handleUploadSubmit, canSubmitUpload, the file
            field below, useUploadAudioFile) is left in place, unused, so this
            can be re-enabled by restoring the ToggleButtonGroup below without
            rebuilding anything.
          */}
          <TextField
            label={t('importAudioTitleLabel', 'Title')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            disabled={pending}
          />
          {mode === 'url' && (
            <TextField
              label={t('importAudioUrlLabel', 'Audio URL')}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              fullWidth
              disabled={isPending}
              placeholder="https://..."
            />
          )}
          {mode === 'upload' && (
            <TextField
              type="file"
              label={t('importAudioFileLabel', 'Audio File')}
              slotProps={{
                htmlInput: { accept: 'audio/*' },
                inputLabel: { shrink: true },
              }}
              onChange={(e) => {
                const input = e.target as HTMLInputElement
                setFile(input.files?.[0] ?? null)
              }}
              fullWidth
              disabled={isUploadPending}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={pending}>
          {t('importAudioCancel', 'Cancel')}
        </Button>
        <Button
          onClick={mode === 'url' ? handleSubmit : handleUploadSubmit}
          variant="contained"
          disabled={mode === 'url' ? !canSubmit : !canSubmitUpload}
          aria-label={t('importAudioSubmit', 'Import')}
        >
          {pending ? <CircularProgress size={24} /> : t('importAudioSubmit', 'Import')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
